/**
 * ConversationsPanel — chat history list, lives INSIDE the workbench
 * chat view (not in the global sidebar anymore). The previous
 * SidebarRecents was the wrong surface: it bled chat-history concerns
 * into every page of the app and offered no preview, no delete.
 *
 * Layout: list of conversation rows, each with title + lazy-fetched
 * preview snippet (first user message) + last_active relative time +
 * delete button. Clicking a row swaps the workbench's active
 * conversation. A "+ New chat" button at the top starts a fresh draft.
 *
 * Preview snippets are fetched per-row via fetchConversation and
 * cached under a separate React Query key. For ~15 rows this is fine
 * over LAN — if rosters grow we'd push the snippet into the list
 * response server-side (Nova OS contract change).
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/OidcProvider";
import { useTenant } from "@/tenants";
import { fetchAgentsInfo } from "@/pages/chat/agentsInfo";
import {
  ConvForbidden,
  ConvNotFound,
  deleteConversation,
  fetchConversation,
  generateConversationTitle,
  renameConversation,
} from "@/pages/chat/api";
import { fetchRecentsAcrossSubjects } from "@/pages/chat/recentsFanOut";
import {
  getRunningConversationIds,
  subscribeProgress,
} from "@/pages/chat/progressStore";
import type { Conversation, ConversationDetail } from "@/pages/chat/types";

interface Props {
  /** The currently active conversation id (highlights its row). */
  activeConversationId: string | null;
  /** Switch to an existing conversation. */
  onSelect: (id: string, agentId?: string) => void;
  /** Start a new draft conversation. */
  onNewConversation: () => void;
}

export function ConversationsPanel({
  activeConversationId,
  onSelect,
  onNewConversation,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { state, authHeader } = useAuth();
  const tenant = useTenant();
  const qc = useQueryClient();

  // Same agent-id union as SidebarRecents: tenant.subjects plus the
  // AgentPicker-registered agents. Without this, conversations against
  // `default` (the common pick on Bosong) wouldn't show up at all.
  const { data: agentsInfo } = useQuery({
    queryKey: ["nova-agents-info"],
    queryFn: () => fetchAgentsInfo(authHeader),
    enabled: state.status === "authenticated",
    staleTime: 60_000,
  });
  const agentIds = useMemo(() => {
    const set = new Set<string>(tenant.subjects.map((s) => s.agentId));
    for (const id of agentsInfo?.agentIds ?? []) set.add(id);
    return [...set];
  }, [tenant.subjects, agentsInfo?.agentIds]);

  const labelByAgentId = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of tenant.subjects) map.set(s.agentId, s.labelKey);
    return map;
  }, [tenant.subjects]);

  const recents = useQuery({
    queryKey: ["chat-recents", agentIds.join(",")],
    queryFn: () => fetchRecentsAcrossSubjects(authHeader, agentIds, 30),
    enabled: state.status === "authenticated" && agentIds.length > 0,
    refetchInterval: (query) => (query.state.error ? false : 30_000),
    retry: false,
    staleTime: 15_000,
  });

  // In-flight conversations that aren't in the recents list yet (#28): a
  // run only persists its conversation when it completes, so a brand-new
  // chat the user switched away from would otherwise be unreachable.
  // Surface them as synthetic rows the user can click back to (their
  // workspace is kept alive by TeacherWorkbenchPage).
  //
  // The row must survive run COMPLETION too: the recents list refetches on
  // an interval, so dropping the row the moment `running` flips false left
  // a window where the conversation vanished entirely until a refresh.
  // Track every id seen running this session and keep its row until the
  // real conversation shows up in the list; completion also triggers an
  // immediate refetch to keep that window short.
  const runningConvIds = useSyncExternalStore(
    subscribeProgress,
    getRunningConversationIds,
    getRunningConversationIds,
  );
  const seenRunningRef = useRef<Set<string>>(new Set());
  for (const id of runningConvIds) if (id) seenRunningRef.current.add(id);

  const prevRunningRef = useRef<string[]>([]);
  useEffect(() => {
    const prev = prevRunningRef.current;
    prevRunningRef.current = runningConvIds;
    if (prev.some((id) => !runningConvIds.includes(id))) {
      void qc.invalidateQueries({ queryKey: ["chat-recents"] });
    }
  }, [runningConvIds, qc]);

  const phantomRunning = useMemo(
    () =>
      [...seenRunningRef.current].filter(
        (id) => !(recents.data ?? []).some((c) => c.id === id),
      ),
    // seenRunningRef grows when runningConvIds changes; recents.data
    // shrinkage is what retires rows.
    [runningConvIds, recents.data], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const remove = useMutation({
    mutationFn: (id: string) => deleteConversation(authHeader, id),
    onSuccess: (_d, id) => {
      // Drop the row from the cached recents list without a full refetch.
      qc.setQueryData<Conversation[] | undefined>(
        ["chat-recents", agentIds.join(",")],
        (prev) => prev?.filter((c) => c.id !== id),
      );
      // If the deleted thread was active, start a fresh draft.
      if (activeConversationId === id) onNewConversation();
    },
  });

  function handleDelete(c: Conversation) {
    // Use whatever's already cached as the row's first user message —
    // mirrors the row's derived title so the confirm copy reads
    // naturally. Falls back to the i18n "Untitled chat" string when
    // we don't have a snippet yet (rare; the row is on-screen so its
    // detail query has usually resolved). Never expose the conversation
    // id to the user.
    const cached = qc.getQueryData<ConversationDetail | null>([
      "chat-conv-detail",
      c.id,
    ]);
    const firstUser = cached?.messages.find((m) => m.role === "user")?.content;
    const title =
      c.title?.trim() ||
      (firstUser ? truncate(firstUser.trim(), 40) : "") ||
      String(t("chat.history.untitled"));
    const ok = window.confirm(t("chat.history.deleteConfirm", { title }));
    if (!ok) return;
    remove.mutate(c.id);
  }

  return (
    <section className="teacher-panel teacher-conversations">
      <div className="teacher-panel__header teacher-panel__header--row">
        <div>
          <p className="teacher-eyebrow">{t("chat.history.eyebrow")}</p>
          <h2>{t("chat.history.heading")}</h2>
        </div>
        <button
          type="button"
          className="teacher-pill teacher-pill--button"
          onClick={onNewConversation}
        >
          {t("chat.history.newChat")}
        </button>
      </div>

      <div className="teacher-panel-scroll">
        {recents.isLoading && (
          <p className="teacher-empty">{t("chat.history.loading")}</p>
        )}
        {!recents.isLoading &&
          (recents.data?.length ?? 0) === 0 &&
          phantomRunning.length === 0 && (
            <p className="teacher-empty">{t("chat.history.empty")}</p>
          )}
        <ul className="teacher-conversation-list">
          {phantomRunning.map((id) => (
            <li
              key={id}
              className={`teacher-conversation-row is-generating ${
                id === activeConversationId ? "is-active" : ""
              }`.trim()}
            >
              <button
                type="button"
                className="teacher-conversation-row__main"
                onClick={() => onSelect(id)}
                aria-current={id === activeConversationId ? "page" : undefined}
              >
                <div className="teacher-conversation-row__title-row">
                  <span className="teacher-conversation-row__title">
                    {runningConvIds.includes(id)
                      ? t("chat.history.generating")
                      : t("chat.history.justFinished")}
                  </span>
                </div>
              </button>
            </li>
          ))}
          {recents.data?.map((c) => (
            <ConversationRow
              key={c.id}
              conversation={c}
              isActive={c.id === activeConversationId}
              agentLabelKey={labelByAgentId.get(c.agent_id)}
              onSelect={() => onSelect(c.id, c.agent_id)}
              onDelete={() => handleDelete(c)}
              authHeader={authHeader}
            />
          ))}
        </ul>
      </div>
    </section>
  );
}

interface RowProps {
  conversation: Conversation;
  isActive: boolean;
  agentLabelKey: string | undefined;
  onSelect: () => void;
  onDelete: () => void;
  authHeader: Record<string, string>;
}

// Conversations we've already fired auto-title generation for, so a
// title-less conversation gets summarized exactly once (across rows and
// re-renders) — never on every list render.
const autoTitledConvIds = new Set<string>();

function ConversationRow({
  conversation,
  isActive,
  agentLabelKey,
  onSelect,
  onDelete,
  authHeader,
}: RowProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  // Only surface a subject label (chemistry, history, …). The generic
  // router agent (`nova-orchestrator` / `default`) is an implementation
  // detail — its raw id is noise on every row, so no pill when there's
  // no meaningful subject label.
  const pillText = agentLabelKey ? String(t(agentLabelKey)) : null;

  // Inline title editing (rename): seed the input from the currently
  // shown title, commit on Enter/blur, cancel on Escape. On success we
  // patch the cached recents rows in place so the new title shows
  // immediately without a refetch.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const rename = useMutation({
    mutationFn: (nextTitle: string) =>
      renameConversation(authHeader, conversation.id, nextTitle),
    onSuccess: (updated) => {
      qc.setQueriesData<Conversation[] | undefined>(
        { queryKey: ["chat-recents"] },
        (prev) =>
          prev?.map((c) =>
            c.id === conversation.id ? { ...c, title: updated.title } : c,
          ),
      );
      setEditing(false);
    },
  });

  // Lazy fetch of the conversation detail so we can derive a
  // user-meaningful title + preview pair. Cached separately from the
  // recents list itself so deleting a row doesn't waste the prefetched
  // details for unrelated rows.
  const detail = useQuery<ConversationDetail | null>({
    queryKey: ["chat-conv-detail", conversation.id],
    queryFn: async () => {
      try {
        return await fetchConversation(authHeader, conversation.id);
      } catch (e) {
        if (e instanceof ConvNotFound || e instanceof ConvForbidden) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Title: prefer the Nova-OS-set conversation.title when it's a real
  // string (title-gen agent output), otherwise derive from the first
  // user message — that's what the teacher asked, the most natural
  // label. The conversation.id hash was the previous fallback and is
  // useless to the teacher; if neither title nor a user message is
  // available, show "Untitled chat" instead of leaking the id.
  const firstUserText = detail.data?.messages
    .find((m) => m.role === "user")
    ?.content?.trim();
  const firstAssistantText = detail.data?.messages
    .find((m) => m.role === "assistant")
    ?.content?.trim();

  // Auto-generate a short AI title once a title-less conversation has its
  // first full turn (user + assistant). Fires once per conversation (the
  // module Set dedupes); the summarizer degrades to the first user line
  // server-side, so a failure just keeps the existing fallback title.
  useEffect(() => {
    if (conversation.title?.trim()) return;
    if (!firstUserText || !firstAssistantText) return;
    if (autoTitledConvIds.has(conversation.id)) return;
    autoTitledConvIds.add(conversation.id);
    void generateConversationTitle(
      authHeader,
      firstUserText,
      firstAssistantText,
      i18n.language,
    )
      .then((generated) => {
        const next = generated?.trim();
        if (next && next !== conversation.title?.trim()) rename.mutate(next);
      })
      .catch(() => {
        autoTitledConvIds.delete(conversation.id); // allow a retry on next mount
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id, conversation.title, firstUserText, firstAssistantText]);

  const title =
    conversation.title?.trim() ||
    (firstUserText ? truncate(firstUserText, 60) : null) ||
    String(t("chat.history.untitled"));

  // Preview shows the assistant's first reply — the answer that came
  // back to the teacher's question — clamped to 2 lines via CSS. When
  // the conversation has a user message but no assistant reply yet,
  // surface the "No reply yet" copy so the row doesn't look empty;
  // when we're still loading detail, leave the slot collapsed.
  let preview: string | null = null;
  if (detail.data) {
    if (firstAssistantText) {
      preview = truncate(firstAssistantText, 160);
    } else if (firstUserText) {
      preview = String(t("chat.history.noReplyYet"));
    }
  }

  const ageText = relativeAge(conversation.last_active_at, i18n.language, t);

  function startEdit() {
    // Seed with the current conversation title if the user set one,
    // otherwise the derived first-message title — a sensible starting
    // point they can edit rather than a blank field.
    setDraft(conversation.title?.trim() || (title === String(t("chat.history.untitled")) ? "" : title));
    setEditing(true);
  }
  function commit() {
    // Enter and onBlur can both fire commit() for the same edit (Enter
    // triggers mutate → input disables → blur). Guard against the double
    // rename request while one is already in flight.
    if (rename.isPending) return;
    const next = draft.trim();
    if (!next || next === (conversation.title?.trim() ?? "")) {
      setEditing(false);
      return;
    }
    rename.mutate(next);
  }

  return (
    <li
      className={`teacher-conversation-row ${
        isActive ? "is-active" : ""
      }`.trim()}
    >
      {editing ? (
        <div className="teacher-conversation-row__main teacher-conversation-row__editing">
          <input
            autoFocus
            className="teacher-conversation-row__title-input"
            value={draft}
            maxLength={120}
            disabled={rename.isPending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
              }
            }}
            onBlur={commit}
            aria-label={String(t("chat.history.rename"))}
          />
        </div>
      ) : (
        <button
          type="button"
          className="teacher-conversation-row__main"
          onClick={onSelect}
          aria-current={isActive ? "page" : undefined}
        >
          <div className="teacher-conversation-row__title-row">
            <span className="teacher-conversation-row__title">{title}</span>
            {pillText && (
              <span className="teacher-conversation-row__agent">{pillText}</span>
            )}
          </div>
          {preview && (
            <div className="teacher-conversation-row__preview">{preview}</div>
          )}
          <div className="teacher-conversation-row__meta">
            <span>{ageText}</span>
            <span>
              {t("chat.history.messageCount", {
                count: conversation.message_count,
              })}
            </span>
          </div>
        </button>
      )}
      {!editing && (
        <button
          type="button"
          className="teacher-conversation-row__rename"
          onClick={startEdit}
          aria-label={t("chat.history.rename")}
          title={t("chat.history.rename")}
        >
          ✎
        </button>
      )}
      <button
        type="button"
        className="teacher-conversation-row__delete"
        onClick={onDelete}
        aria-label={t("chat.history.delete")}
        title={t("chat.history.delete")}
      >
        ×
      </button>
    </li>
  );
}

/** Trim a free-form message body for use as a row title or preview.
 *  Collapses runs of whitespace so multi-line user messages don't
 *  produce gappy single-line titles, then ellipsis-truncates at the
 *  given limit. Returned strings have a single-space separator between
 *  formerly-separate paragraphs — good enough for a row label. */
function truncate(text: string, limit: number): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > limit ? `${flat.slice(0, limit).trimEnd()}…` : flat;
}

/** Format an ISO timestamp as a short, locale-appropriate relative
 *  age string. Falls back to a date when older than 7 days so distant
 *  rows don't all read "N weeks ago" indistinguishably. */
function relativeAge(
  iso: string,
  locale: string,
  t: (k: string, v?: Record<string, unknown>) => string,
): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return t("chat.history.age.justNow");
  if (min < 60) return t("chat.history.age.minutes", { n: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("chat.history.age.hours", { n: hr });
  const d = Math.floor(hr / 24);
  if (d < 7) return t("chat.history.age.days", { n: d });
  try {
    return new Date(iso).toLocaleDateString(
      locale.toLowerCase().startsWith("zh") ? "zh-CN" : "en-US",
    );
  } catch {
    return new Date(iso).toLocaleDateString();
  }
}
